import { Button, ButtonGroup, Dialog, PageTabs, PageTabsList, PageTabsTrigger, SearchInput } from "@tellescope/ui";
import { Spinner, Select } from "@tellescope/design-system";
import { PageTabsHeader } from "../../components";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Filter } from "lucide-react";
import { useTemplatesPage } from "./useTemplatesPage";
import { TemplatesFilterDialog } from "./filters/TemplatesFilterDialog";
import { SaveTemplateViewDialog } from "./filters/SaveTemplateViewDialog";
import { TemplateCard } from "./TemplateCard";
import { useCallback, useMemo } from "react";
import { TemplateFormDialogContents } from "./TemplateFormDialogContents";

const DEFAULT_VIEW_ID = "default";

export const TemplatesPage = () => {
  const { t } = useTranslation("templates");
  const [searchParams, setSearchParams] = useSearchParams();
  const { templateId, duplicateTemplateId, create } = useMemo(() => {
    return {
      templateId: searchParams.get("templateId"),
      duplicateTemplateId: searchParams.get("duplicateTemplateId"),
      create: !!searchParams.get("create"),
    };
  }, [searchParams]);
  const mode = duplicateTemplateId ? "duplicate" : create ? "create" : templateId ? "edit" : undefined;

  const {
    search,
    setSearch,
    filters,
    activeFiltersCount,
    views,
    viewId,
    onSelectView,
    filterDialogOpen,
    setFilterDialogOpen,
    saveDialogOpen,
    setSaveDialogOpen,
    onApplyFilters,
    onSaveAsView,
    onConfirmSaveView,
    isSavingView,
    templates,
    templatesLoading,
    canUpdate,
    canCreate,
    canDelete,
    scrollContainerRef,
    thumbRef,
    handleScroll,
    sentinelRef,
    deleteTemplateMutation,
    updateTemplateMutation,
  } = useTemplatesPage();

  const closeTemplateDialog = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("templateId");
      prev.delete("duplicateTemplateId");
      prev.delete("create");
      return prev;
    });
  }, [setSearchParams]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <PageTabsHeader className="pr-3">
        <PageTabs value="templates" className="h-full">
          <PageTabsList>
            <PageTabsTrigger value="templates">{t("Templates")}</PageTabsTrigger>
            {/* <PageTabsTrigger value="snippets" asChild> */}
            {/*   <Link to="/templates/snippets">{t("Snippets")}</Link> */}
            {/* </PageTabsTrigger> */}
          </PageTabsList>
        </PageTabs>
      </PageTabsHeader>
      <section className="flex justify-between gap-2 border-b p-6 items-center">
        <div className="space-y-1">
          <h1 className="text-3xl font-semibold">{t("Templates")}</h1>
          <h3 className="text-muted-foreground">{t("Message template configuration and settings")}</h3>
        </div>
        {canCreate && (
          <Button asChild data-testid="templates-add-button">
            <Link to=".?create=true" replace>
              {t("Add Template")}
            </Link>
          </Button>
        )}
      </section>
      <section className="pt-6 px-6 pb-2 space-y-2">
        <div className="flex w-full gap-2">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName="grow h-auto"
            className="h-7"
            placeholder={t("Search templates...")}
            data-testid="templates-search-input"
          />
          <ButtonGroup>
            <Select value={viewId} onValueChange={onSelectView}>
              <Select.Trigger
                data-trackingid="templates-view-selector"
                data-testid="templates-view-selector"
                className="w-min"
              >
                <Select.Value placeholder={t("All")} className="w-min" />
              </Select.Trigger>
              <Select.Content>
                <Select.Item value={DEFAULT_VIEW_ID}>{t("All")}</Select.Item>
                {views.map((view) => (
                  <Select.Item key={view.id} value={view.id} data-testid={`templates-view-item-${view.id}`}>
                    {view.title}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
            <Button
              size="sm-icon"
              variant="outline"
              className="h-8 px-2 w-auto"
              onClick={() => setFilterDialogOpen(true)}
              data-trackingid="templates-filter-button"
              data-testid="templates-filter-button"
            >
              <Filter />
              {activeFiltersCount > 0 && (
                <span className="ml-1 rounded-full bg-primary px-1.5 text-xs text-primary-foreground">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </ButtonGroup>
          {viewId === DEFAULT_VIEW_ID && activeFiltersCount > 0 && (
            <Button
              variant="outline"
              className="h-8"
              onClick={() => onSaveAsView(filters)}
              data-trackingid="templates-save-as-view-button"
              data-testid="templates-save-as-view-button"
            >
              {t("Save as View")}
            </Button>
          )}
        </div>
      </section>
      <section className="relative grow min-h-0 @container px-6 pb-6">
        {templatesLoading ? (
          <div className="flex flex-col items-center justify-center gap-2">
            <Spinner size="md" />
            <p className="text-sm text-muted-foreground">{t("Loading...")}</p>
          </div>
        ) : (
          <>
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              data-testid="templates-grid"
              className="grid grid-cols-1 @2xl:grid-cols-2 @4xl:grid-cols-3 @7xl:grid-cols-4 gap-4 py-4 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: "none" }}
            >
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onClick={canUpdate ? { to: `.?templateId=${template.id}`, replace: true } : undefined}
                  onDelete={canDelete ? () => deleteTemplateMutation.mutateAsync(template.id) : undefined}
                  onDuplicate={canCreate ? { to: `.?duplicateTemplateId=${template.id}`, replace: true } : undefined}
                  onArchiveToggle={
                    canUpdate
                      ? (archived) =>
                          updateTemplateMutation.mutateAsync({
                            id: template.id,
                            archivedAt: archived ? new Date() : "",
                          })
                      : undefined
                  }
                />
              ))}
              <div ref={sentinelRef} className="col-span-full h-px" aria-hidden="true" />
            </div>
            <div
              ref={thumbRef}
              className="absolute top-0 right-1 w-1.5 bg-black/40 rounded-full pointer-events-none opacity-0 transition-opacity duration-300 z-[10000]"
            />
          </>
        )}
      </section>
      <TemplatesFilterDialog
        open={filterDialogOpen}
        onOpenChange={setFilterDialogOpen}
        filters={filters}
        onApply={onApplyFilters}
      />
      <SaveTemplateViewDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        onSave={onConfirmSaveView}
        isSaving={isSavingView}
      />
      <Dialog open={!!mode} onOpenChange={(open) => !open && closeTemplateDialog()}>
        <TemplateFormDialogContents
          templateId={templateId || duplicateTemplateId || undefined}
          mode={mode}
          onSuccess={closeTemplateDialog}
        />
      </Dialog>
    </div>
  );
};
